import React, { useEffect, useState, useCallback } from 'react';

const MouseBubble: React.FC = () => {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Throttle mouse movement for better performance
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isVisible) return;
    
    // Use requestAnimationFrame for smoother animation
    requestAnimationFrame(() => {
      setPosition({ x: e.clientX, y: e.clientY });
    });
    
    // Optimized clickable element detection with early returns
    const target = e.target as HTMLElement;
    
    // Quick tag name checks first
    if (target.tagName === 'A' || target.tagName === 'BUTTON') {
      setIsHovering(true);
      return;
    }
    
    // Check for closest clickable elements
    if (target.closest('a') || target.closest('button') || target.closest('[role="button"]')) {
      setIsHovering(true);
      return;
    }
    
    // Check input types
    const inputType = (target as HTMLInputElement).type;
    if (inputType === 'submit' || inputType === 'button') {
      setIsHovering(true);
      return;
    }
    
    // Additional checks (only if needed)
    if (
      target.onclick !== null ||
      target.style.cursor === 'pointer' ||
      target.closest('label') ||
      target.closest('[data-clickable]')
    ) {
      setIsHovering(true);
    }
  }, [isVisible]);

  useEffect(() => {
    // Only enable on desktop devices
    const isTouchDevice = 'ontouchstart' in window || 
                         navigator.maxTouchPoints > 0 ||
                         window.matchMedia('(hover: none)').matches;
    
    if (isTouchDevice) {
      setIsVisible(false);
      return;
    }

    // Debounced mouse leave handler
    let leaveTimeout: ReturnType<typeof setTimeout>;
    const handleMouseLeave = () => {
      leaveTimeout = setTimeout(() => setIsVisible(false), 100);
    };
    
    const handleMouseEnter = () => {
      if (leaveTimeout) clearTimeout(leaveTimeout);
      setIsVisible(true);
    };

    // Create and add styles once
    const style = document.createElement('style');
    style.textContent = `
      @media (hover: hover) and (pointer: fine) {
        a, button, [role="button"], input[type="submit"], 
        input[type="button"], label, [data-clickable] {
          cursor: none !important;
        }
        
        input:not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"]),
        textarea,
        [contenteditable="true"],
        select {
          cursor: text !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Use passive event listener for better scroll performance
    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener('mousemove', handleMouseMove, opts);
    document.addEventListener('mouseenter', handleMouseEnter, opts);
    document.addEventListener('mouseleave', handleMouseLeave, opts);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.head.removeChild(style);
      if (leaveTimeout) clearTimeout(leaveTimeout);
    };
  }, [handleMouseMove]);

  if (!isVisible) return null;

  return (
    <div
      className="fixed pointer-events-none z-[9999] transition-transform duration-75 ease-out will-change-transform"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -50%)',
      }}
    >
      {/* Outer expanding bubble */}
      <div
        className={`absolute rounded-full transition-all duration-200 ease-out ${
          isHovering 
            ? 'w-12 h-12 -ml-6 -mt-6' 
            : 'w-8 h-8 -ml-4 -mt-4'
        }
        bg-gray-300/30 border border-gray-200/40
        dark:bg-gray-600/30 dark:border-gray-700/40`}
        style={{
          boxShadow: isHovering 
            ? '0 0 20px rgba(255, 255, 255, 0.3)' 
            : '0 0 10px rgba(255, 255, 255, 0.1)',
        }}
      />
      
      {/* Inner dot */}
      <div
        className={`absolute rounded-full transition-all duration-100 ease-out ${
          isHovering 
            ? 'w-3 h-3' 
            : 'w-2 h-2'
        }
        bg-gray-100 dark:bg-gray-800`}
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
      
      {/* Pulse effect when hovering */}
      {isHovering && (
        <div
          className="absolute rounded-full animate-ping"
          style={{
            width: '60px',
            height: '60px',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            animationDuration: '1.5s',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }}
        />
      )}
    </div>
  );
};

export default MouseBubble;